import schedulesViewStateStore from "../store/store";
import ShiftUtils from "sh-application/utility/ShiftUtils";
import { DataProcessingHelpers } from "sh-services";
import { IOpenShiftEntity, OpenShiftEntity, ShiftStates } from "sh-models";
import { Moment } from "moment";
import { orchestrator } from "satcheljs";
import updateOpenShiftsInView from "../mutators/openShifts/updateOpenShiftsInView";
import deleteOpenShiftsInView from "../mutators/openShifts/deleteOpenShiftsInView";
import { updateOpenShiftsInStore } from "sh-open-shift-store";
import ECSConfigService from "sh-services/lib/ecsconfigservice/ECSConfigService";
import ECSConfigKey from "sh-services/lib/ecsconfigservice/ECSConfigKey";

/**
 * When updates to open shifts are triggered via the UI or via sync notifications, this orchestrator will be triggered.
 * It will parse the updated open  and pass those that fit into the current view to the updateOpenShiftsInView() mutator, which
 * will update the map of open shifts in the local view store
 */
export default orchestrator(updateOpenShiftsInStore, async actionMessage => {
    const store = actionMessage.viewState ? actionMessage.viewState : schedulesViewStateStore();
    const viewStart: Moment = store.viewStartDate;
    const viewEnd: Moment = store.viewEndDate;
    const updatedOpenShiftsForView: IOpenShiftEntity[] = DataProcessingHelpers.getDisplayableShiftsInScheduleRange(actionMessage.updatedOpenShifts, viewStart, viewEnd) as IOpenShiftEntity[];

    // We need to handle the special case where updated open shifts that were previously in the view have been moved out of the view. These open shifts will not count as
    // displayable shifts in the schedule range, so we detect them by checking if their ids are present in the view store
    if (actionMessage.updatedOpenShifts) {
        for (let i = 0; i < actionMessage.updatedOpenShifts.length; i++) {
            const openShift: IOpenShiftEntity = actionMessage.updatedOpenShifts[i];
            if (store.openShiftsInView.has(openShift.id) && !ShiftUtils.isShiftInList(updatedOpenShiftsForView, openShift)) {
                updatedOpenShiftsForView.push(openShift);
            }
        }
    }

    // When HideZeroSlotOpenShifts is enabled, hide any open shift whose slots are all filled.
    // Applies to all assignment paths (auto-assign, context menu) and to page load
    // (entities with openSlots=0 but sharedChanges.openSlots>0 that pass the displayable filter
    // are caught here before reaching updateOpenShiftsInView).
    // Handles cases by whether the entity has a published component:
    //   - Published (os.isPublished=true) or published-with-draft (sharedChanges.isPublished=true):
    //     transform to asterisk so the manager can publish the pending deletion.
    //     Also re-applies this transformation for sync notifications that arrive after GDIDR,
    //     where the entity may temporarily revert to isPublished=false with sharedChanges.isPublished=true.
    //   - Draft-on-draft or pure-draft (no published component): remove from view entirely —
    //     the open shift row should be empty, no asterisk needed.
    if (ECSConfigService.isECSFeatureEnabled(ECSConfigKey.HideZeroSlotOpenShifts)) {
        const shiftsToDeleteFromView: IOpenShiftEntity[] = [];
        // Iterate backwards so splicing by index is safe.
        for (let i = updatedOpenShiftsForView.length - 1; i >= 0; i--) {
            const os = updatedOpenShiftsForView[i] as IOpenShiftEntity;
            if (os && os.openSlots <= 0) {
                const sharedChangesEntity = os.sharedChanges as IOpenShiftEntity | null | undefined;
                const hasPublishedComponent = os.isPublished || (sharedChangesEntity && sharedChangesEntity.isPublished);
                if (hasPublishedComponent) {
                    // Published or published-with-draft open shift: keep as asterisk so the
                    // manager knows a deletion draft is pending publish. Transform to draft-deleted state.
                    // This also re-applies this transformation for sync notifications that arrive
                    // after GDIDR, where the entity may be isPublished=false with sharedChanges.isPublished=true.
                    const existingInView = store.openShiftsInView.get(os.id) as IOpenShiftEntity | undefined;
                    const sharedChanges: IOpenShiftEntity = (existingInView && ShiftUtils.isUnsharedDeletedShift(existingInView))
                        ? ShiftUtils.getSharedChanges(existingInView) as IOpenShiftEntity
                        : OpenShiftEntity.clone(os);
                    const draftDeleted = OpenShiftEntity.clone(os);
                    draftDeleted.state = ShiftStates.Deleted;
                    draftDeleted.isPublished = false;
                    draftDeleted.sharedChanges = sharedChanges;
                    updatedOpenShiftsForView[i] = draftDeleted;
                } else {
                    // Draft-on-draft or pure-draft (no published component):
                    // remove from the open shift row entirely.
                    shiftsToDeleteFromView.push(os);
                    updatedOpenShiftsForView.splice(i, 1);
                }
            }
        }
        if (shiftsToDeleteFromView.length) {
            deleteOpenShiftsInView(shiftsToDeleteFromView, store);
        }
    }

    if (updatedOpenShiftsForView.length) {
        updateOpenShiftsInView(updatedOpenShiftsForView, store);
    }
});